import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Logo } from "../components/seechelle/Logo";
import { LogOut, LayoutDashboard, Inbox, Image as ImageIcon, ExternalLink, ChevronRight } from "lucide-react";
import { SECTIONS } from "./sections";

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    document.title = "See Chelle Admin";
    return () => { document.head.removeChild(m); };
  }, []);
};

export const AdminLayout = () => {
  useNoIndex();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-blush-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-blush-200/70 sticky top-0 z-30" data-testid="admin-header">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold hidden md:inline">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer"
               className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-ink2 hover:text-magenta transition-colors" data-testid="admin-view-site">
              View site <ExternalLink size={13} />
            </a>
            <span className="text-[13px] text-muteink hidden md:inline" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-[13px] text-ink2 hover:text-magenta transition-colors" data-testid="admin-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-[1400px] w-full mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <nav className="md:sticky md:top-24 self-start" aria-label="Admin sections" data-testid="admin-sidebar">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold mb-3 px-3">Overview</div>
          <ul className="space-y-1 mb-6">
            <SidebarLink to="/admin" end icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" />
            <SidebarLink to="/admin/inbox" icon={Inbox} label="Inbox" testid="nav-inbox" />
            <SidebarLink to="/admin/media" icon={ImageIcon} label="Media" testid="nav-media" />
          </ul>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold mb-3 px-3">Content</div>
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => (
              <SidebarLink key={s.key} to={`/admin/content/${s.key}`} label={s.label} testid={`nav-content-${s.key}`} />
            ))}
          </ul>
        </nav>

        {/* Main content */}
        <main className="min-w-0" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const SidebarLink = ({ to, icon: Icon, label, end, testid }) => (
  <li>
    <NavLink to={to} end={end}
             className={({ isActive }) =>
               `group flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[14px] font-body transition-colors ${
                 isActive ? "bg-white text-ink shadow-sm border border-blush-200" : "text-ink2 hover:bg-white/70 hover:text-ink"
               }`}
             data-testid={testid}>
      {({ isActive }) => (
        <>
          <span className="inline-flex items-center gap-2.5">
            {Icon && <Icon size={15} strokeWidth={1.7} className={isActive ? "text-magenta" : "text-muteink"} />}
            {label}
          </span>
          <ChevronRight size={13} className={`opacity-0 group-hover:opacity-40 transition-opacity ${isActive ? "opacity-60" : ""}`} />
        </>
      )}
    </NavLink>
  </li>
);

export default AdminLayout;
